import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../utils/auth';
import { useApiClient } from '../utils/apiClient';

export const useAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const api = useApiClient();

    const checkAuth = useCallback(async () => {
        try {
            setIsLoading(true);
            const token = await auth.getValidToken();
            const authenticated = !!token && !auth.isTokenExpired(token);
            setIsAuthenticated(authenticated);
            if (authenticated && token) {
                try {
                    const { jwtDecode } = await import('jwt-decode');
                    const decoded = jwtDecode(token);
                    setUser({
                        sub: decoded.sub,
                        email: decoded.email,
                        name: decoded.name,
                        given_name: decoded.given_name,
                        family_name: decoded.family_name,
                        email_verified: decoded.email_verified,
                    });
                } catch (error) {
                    console.error('Error decoding token:', error);
                    setUser(null);
                }
            } else {
                setUser(null);
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            setIsAuthenticated(false);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(() => {
        window.location.href = auth.getCognitoLoginUrl();
    }, []);

    const logout = useCallback(() => {
        auth.clearTokens();
        setIsAuthenticated(false);
        setUser(null);
        navigate('/login');
    }, [navigate]);

    const refreshAuth = useCallback(async () => {
        try {
            const success = await auth.refreshToken();
            if (success) {
                await checkAuth();
                return true;
            }
            return false;
        } catch (error) {
            console.error('Auth refresh failed:', error);
            return false;
        }
    }, [checkAuth]);

    const getValidToken = useCallback(async () => {
        return auth.getValidToken();
    }, []);

    const isTokenExpired = useCallback((token) => {
        return auth.isTokenExpired(token);
    }, []);

    return {
        isAuthenticated,
        isLoading,
        user,
        login,
        logout,
        refreshAuth,
        api,
        getValidToken,
        isTokenExpired,
    };
}; 